import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Brain, 
  Gamepad2, 
  FileText, 
  Target, 
  Users, 
  CheckCircle2,
  ArrowRight,
  GraduationCap,
  Stethoscope,
  Loader2,
  BookOpen,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for uploaded assets
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url.startsWith('/api') ? '' : '/api'}${url}`;
  return `${API}/api/${url}`;
};

// Brand Logo component that uses uploaded logo
const BrandLogo = ({ logoUrl, dark = true }) => (
  <div className="flex items-center gap-2">
    <img src={logoUrl} alt="MedStudyPro" className="h-8 object-contain" />
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ dark = true }) => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left M leg */}
      <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right M leg */}
      <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Center cube - top face */}
      <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
      {/* Center cube - visible faces */}
      <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
      <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
      <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
    </svg>
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Target: Target,
  Gamepad2: Gamepad2,
  FileText: FileText,
  BookOpen: BookOpen,
  Brain: Brain,
  Users: Users,
  Stethoscope: Stethoscope
};

// Default content (fallback if API fails)
const DEFAULT_CONTENT = {
  hero_badge: "Medical Education Platform",
  hero_title: "About MedStudyPro",
  hero_subtitle: "Empowering healthcare professionals and students to master clinical knowledge through interactive learning, adaptive quizzes, and real-world case studies.",
  mission_title: "Our Mission",
  mission_paragraph_1: "MedStudyPro was built to address the challenges healthcare professionals face in maintaining and expanding their clinical knowledge.",
  mission_paragraph_2: "Our platform combines evidence-based content with modern learning science to help you retain information more effectively.",
  features_title: "What You Get",
  features: [
    { icon: "Target", title: "Adaptive Quizzes", description: "Practice with MCQs and varied question types across all clinical topics." },
    { icon: "Gamepad2", title: "Learning Games", description: "Solidify knowledge through flashcards, matching, and word search." },
    { icon: "FileText", title: "Case Studies", description: "Apply clinical reasoning to multi-stage patient scenarios." },
    { icon: "BookOpen", title: "Reference Sheets", description: "Quick-access visual guides and downloadable PDFs." }
  ],
  benefits_title: "Why Choose MedStudyPro?",
  benefits: [
    "Comprehensive question bank covering critical care topics",
    "Track your progress and identify weak areas",
    "Learn at your own pace with on-demand content",
    "Reinforce learning through multiple modalities",
    "Clinical case studies for practical application",
    "Mobile-friendly for studying anywhere"
  ],
  footer_cta_title: "Ready to Elevate Your Clinical Knowledge?",
  footer_cta_subtitle: "Join healthcare professionals who are advancing their careers with MedStudyPro."
};

export default function About() {
  const navigate = useNavigate();
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [founder, setFounder] = useState({ name: '', credentials: '', bio: '', photo_url: null });

  useEffect(() => {
    fetchContent();
    fetchLogo();
    fetchFounder();
  }, []);

  const fetchLogo = async () => {
    try {
      const res = await fetch(`${API}/api/logo-settings`);
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logo_url || data.logo_light_url);
      }
    } catch (err) {
      console.error('Failed to fetch logo:', err);
    }
  };

  const fetchFounder = async () => {
    try {
      const res = await fetch(`${API}/api/founder`);
      if (res.ok) {
        const data = await res.json();
        setFounder(data);
      }
    } catch (err) {
      console.error('Failed to fetch founder info:', err);
    }
  };

  const fetchContent = async () => {
    try {
      const res = await fetch(`${API}/api/about/content`);
      if (res.ok) {
        const data = await res.json();
        setContent(data);
      }
    } catch (err) {
      console.error('Failed to fetch About content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#192A3F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  // Helper to render the logo
  const renderLogo = (dark = true) => {
    if (logoUrl) {
      return <BrandLogo logoUrl={getFullUrl(logoUrl)} dark={dark} />;
    }
    return <DefaultLogo dark={dark} />;
  };

  return (
    <div className="min-h-screen bg-[#192A3F]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <button onClick={() => navigate('/')}>
          {renderLogo(true)}
        </button>
        <div className="flex items-center gap-6">
          <button 
            onClick={() => navigate('/pricing')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            Pricing
          </button>
          <button 
            onClick={() => navigate('/contact')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            Contact
          </button>
          <Button
            onClick={handleLogin}
            className="bg-[#FF9E00] hover:bg-[#E88D00] text-white px-6 py-2 rounded-full font-semibold text-sm"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-5xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9E00]/20 rounded-full text-[#FF9E00] text-sm font-medium mb-6">
          <GraduationCap className="w-4 h-4" />
          {content.hero_badge}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          {content.hero_title}
        </h1>
        <p className="text-xl text-white/60 max-w-2xl mx-auto mb-8">
          {content.hero_subtitle}
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Button 
            onClick={handleLogin} 
            className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-12 px-8 rounded-full font-semibold"
            data-testid="about-get-started-btn"
          >
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button 
            onClick={() => navigate('/pricing')} 
            className="bg-[#264B73] hover:bg-[#1E3A5C] text-white h-12 px-8 rounded-full font-semibold"
          >
            View Pricing
          </Button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-[#264B73] rounded-2xl p-6 text-center">
            <Stethoscope className="w-8 h-8 text-[#FF9E00] mx-auto mb-3" />
            <div className="text-2xl font-bold text-white">ICU</div>
            <div className="text-sm text-white/60">Critical Care Focus</div>
          </div>
          <div className="bg-[#264B73] rounded-2xl p-6 text-center">
            <Users className="w-8 h-8 text-blue-400 mx-auto mb-3" />
            <div className="text-2xl font-bold text-white">All Levels</div>
            <div className="text-sm text-white/60">Students & Clinicians</div>
          </div>
          <div className="bg-[#264B73] rounded-2xl p-6 text-center">
            <Brain className="w-8 h-8 text-purple-400 mx-auto mb-3" />
            <div className="text-2xl font-bold text-white">Adaptive</div>
            <div className="text-sm text-white/60">Personalized Learning</div>
          </div>
          <div className="bg-[#264B73] rounded-2xl p-6 text-center">
            <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <div className="text-2xl font-bold text-white">Track</div>
            <div className="text-sm text-white/60">Progress & Weak Areas</div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="bg-gradient-to-b from-[#1E3A5C] to-[#192A3F] py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">{content.mission_title}</h2>
          <div className="grid md:grid-cols-2 gap-8 text-white/70">
            <p className="leading-relaxed">{content.mission_paragraph_1}</p>
            <p className="leading-relaxed">{content.mission_paragraph_2}</p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-[#1E3A5C] text-center mb-10">{content.features_title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(content.features || []).map((feature, idx) => {
              const IconComponent = ICON_MAP[feature.icon] || Target;
              const colors = [
                { bg: 'bg-orange-100', icon: 'text-[#FF9E00]' },
                { bg: 'bg-purple-100', icon: 'text-purple-600' },
                { bg: 'bg-blue-100', icon: 'text-blue-600' },
                { bg: 'bg-green-100', icon: 'text-green-600' }
              ];
              const color = colors[idx % colors.length];
              
              return (
                <div key={idx} className="bg-[#F8FAFD] rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className={`p-3 ${color.bg} rounded-xl w-fit mb-4`}>
                    <IconComponent className={`w-6 h-6 ${color.icon}`} />
                  </div>
                  <h3 className="font-semibold text-[#1E3A5C] mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-[#F8FAFD] py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-[#1E3A5C] text-center mb-10">{content.benefits_title}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {(content.benefits || []).map((benefit, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-xl">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-[#1E3A5C]">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* About the Founder Section */}
      {(founder.name || founder.bio) && (
        <div className="bg-white py-16 border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-[#1E3A5C] text-center mb-10">About the Founder</h2>
            <div className="grid md:grid-cols-3 gap-8 items-start">
              {/* Photo Column */}
              <div className="md:col-span-1 flex justify-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center shadow-lg">
                  {founder.photo_url ? (
                    <img 
                      src={getFullUrl(founder.photo_url)} 
                      alt={founder.name || 'Founder'} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1E3A5C] to-[#264B73] flex items-center justify-center">
                      <Users className="w-16 h-16 text-white/40" />
                    </div>
                  )}
                </div>
              </div>
              {/* Text Column */}
              <div className="md:col-span-2">
                {founder.name && (
                  <h3 className="text-xl font-bold text-[#1E3A5C] mb-1">{founder.name}</h3>
                )}
                {founder.credentials && (
                  <p className="text-[#FF9E00] font-medium mb-4">{founder.credentials}</p>
                )}
                {founder.bio && (
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">{founder.bio}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-[#1E3A5C] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {content.footer_cta_title}
          </h2>
          <p className="text-white/60 text-lg mb-8">
            {content.footer_cta_subtitle}
          </p>
          <Button
            onClick={handleLogin}
            className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-14 px-10 rounded-full font-semibold text-base shadow-lg shadow-[#FF9E00]/20"
          >
            Start Learning Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0D1520] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {renderLogo(true)}
            <div className="flex items-center gap-8">
              <button onClick={() => navigate('/')} className="text-white/60 hover:text-white text-sm">Home</button>
              <button onClick={() => navigate('/pricing')} className="text-white/60 hover:text-white text-sm">Pricing</button>
              <button onClick={() => navigate('/contact')} className="text-white/60 hover:text-white text-sm">Contact</button>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">© 2026 MedStudyPro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
